import { ChangeEventHandler, ReactNode, forwardRef } from 'react';

import { addStyles } from '../../utils/helpers';

export type RadioButtonSize = 'medium' | 'large';

export interface RadioButtonProperties {
  /** The 'name' attribute for the html input element, used for forms. */
  name: string;
  /** The 'value' attribtue for the html input element */
  value: string;
  /** The label text to display next to the component */
  label: string;
  /** The radio's checked state */
  checked?: boolean;
  /** Label for accessibility purposes */
  ariaLabel?: string;
  /** Whether the label should be visible or not */
  hideLabel?: boolean;
  /** Callback for when the checked value changes */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** Whether input element is disabled or not */
  isDisabled?: boolean;
  /** The id of the html input element */
  id?: string;
  /** Text to display below the label */
  helperText?: string;
  /** Optional child component to display below the label */
  children?: ReactNode;
  /** Size of the text for the radio button */
  size?: RadioButtonSize;
}

const defaultStyles =
  'cursor-pointer border-1 border-gray-600 hover:border-primary mt-0.5 size-5 bg-none outline-midnight-900';
const hoverStyles = 'hover:border-primary';
const disabledStyles =
  'disabled:checked:shadow-[0px_0px_0px_4px_inset] disabled:border-gray-500 disabled:bg-gray-100 disabled:checked:bg-gray-500 disabled:checked:shadow-white disabled:cursor-not-allowed';
const checkedStyles =
  'checked:shadow-white checked:shadow-[0px_0px_0px_4px_inset] checked:bg-primary checked:border-primary ';

const getTextSizeClasses = (size: RadioButtonSize) => {
  return size === 'medium' ? 'text-sm leading-6' : 'text-base';
};

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProperties>(
  ({ hideLabel = false, isDisabled = false, ...props }, ref) => {
    const {
      label,
      name,
      value,
      onChange,
      ariaLabel = label,
      id,
      helperText,
      checked,
      children,
      size = 'large',
    } = props;
    return (
      <div className="flex flex-col">
        <div className="flex items-center ">
          <label
            className={addStyles(['flex justify-center gap-2.5', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'])}
            htmlFor={id}
          >
            <input
              className={addStyles([defaultStyles, hoverStyles, checkedStyles, disabledStyles])}
              type="radio"
              onChange={onChange}
              value={value}
              name={name}
              aria-label={ariaLabel}
              disabled={isDisabled}
              id={id}
              checked={checked}
              ref={ref}
            />
            <div
              className={addStyles(['ml-2', isDisabled ? 'text-gray-600' : 'text-gray-900', getTextSizeClasses(size)])}
            >
              {!hideLabel && <span>{label}</span>}
              {helperText && <p className="text-xs text-gray-800">{helperText}</p>}
              {children}
            </div>
          </label>
        </div>
      </div>
    );
  }
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;
